import { Link } from "react-router-dom";
import { Bookmark, ClipboardList, CalendarClock, MessageSquare, ArrowRight } from "lucide-react";
import { PageHeader } from "@/components/Common";
import { CandidateCard } from "@/components/CandidateCard";
import { MemberGate } from "@/components/RequestDialogs";
import { useAuth } from "@/context/AuthContext";
import { useFetch, useShortlist, isMember } from "@/lib/api";

const Stat = ({ icon: Icon, label, value, to, testId }) => (
  <Link to={to} data-testid={testId} className="lift rounded-md border border-stone-200 bg-white p-6">
    <Icon className="h-5 w-5 text-stone-500" strokeWidth={1.5} />
    <p className="mt-6 font-display text-4xl font-extrabold text-stone-900">{value ?? "–"}</p>
    <p className="mt-1 text-xs font-mono uppercase tracking-wider text-stone-500">{label}</p>
  </Link>
);

export default function Overview() {
  const { user } = useAuth();
  const { data: s } = useFetch("/dashboard/summary");
  const { data: cands } = useFetch("/candidates?status=Available");
  const { ids, toggle } = useShortlist();

  return (
    <div>
      <PageHeader eyebrow={user?.company || "Client portal"} title={`Hello, ${user?.name?.split(" ")[0]}`} desc="Here's what's happening with your virtual workforce." />
      {!isMember(user) && <div className="mb-8"><MemberGate /></div>}
      {s?.interview_usage?.limit != null && (
        <p data-testid="overview-interview-usage" className="mb-6 rounded-md border border-stone-200 bg-white px-4 py-3 text-sm text-stone-700">
          Starter plan: <b>{s.interview_usage.used} of {s.interview_usage.limit}</b> interview requests used this month. <Link to="/app/membership" className="underline">Upgrade for unlimited</Link>
        </p>
      )}
      <div className="grid grid-cols-2 gap-4 lg:grid-cols-4">
        <Stat icon={Bookmark} label="Shortlisted" value={s?.shortlist} to="/app/shortlist" testId="stat-shortlist" />
        <Stat icon={ClipboardList} label="Hire requests" value={s?.hire_requests} to="/app/requests" testId="stat-hires" />
        <Stat icon={CalendarClock} label="Interviews" value={s?.interviews} to="/app/requests" testId="stat-interviews" />
        <Stat icon={MessageSquare} label="Unread messages" value={s?.unread_messages} to="/app/messages" testId="stat-messages" />
      </div>
      <div className="mt-14 flex items-end justify-between">
        <h2 className="font-display text-2xl font-bold text-stone-900">Available now</h2>
        <Link to="/app/candidates" data-testid="overview-browse-all" className="flex items-center gap-1 text-sm font-medium text-slate-900 hover:underline">Browse all <ArrowRight className="h-4 w-4" /></Link>
      </div>
      <div className="mt-6 grid gap-6 md:grid-cols-2 xl:grid-cols-3">
        {(cands || []).slice(0, 3).map((c) => <CandidateCard key={c.id} c={c} shortlisted={ids.includes(c.id)} onToggle={toggle} />)}
      </div>
    </div>
  );
}
